'use client';

import React, { useState } from 'react';
import { Box, Typography, Button, Dialog } from 'src/components';
import { useDeleteProviderCohortMutation } from 'src/api/redux/providerCohorts';
import { useNotification } from 'src/context/notification';
import { useAppDispatch, cleanupDeletedColumnReference } from 'src/redux';

interface DeleteEncounterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  volumeToDelete: {
    id: number;
    label: string;
  } | null;
  onDeleteSuccess?: () => void;
}

const DeleteEncounterDialog: React.FC<DeleteEncounterDialogProps> = ({
  isOpen,
  onClose,
  volumeToDelete,
  onDeleteSuccess,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { setNotification } = useNotification();
  const dispatch = useAppDispatch();

  const [deleteCohort] = useDeleteProviderCohortMutation();

  const handleDeleteConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!volumeToDelete) return;

    setIsDeleting(true);

    try {
      await deleteCohort(volumeToDelete.id.toString()).unwrap();

      // Clean up any column references to this encounter/cohort from Redux and localStorage
      dispatch(cleanupDeletedColumnReference({
        id: volumeToDelete.id,
        type: 'cohort_id'
      }));

      // No manual refresh needed - RTK Query cache auto-invalidates

      // Show success message
      setNotification({
        title: 'Success',
        message: `Volume "${volumeToDelete.label}" has been deleted`,
        type: 'success',
      });

      // Call the onDeleteSuccess callback if provided
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error: any) {
      console.error('Error deleting volume:', error);

      // Show error message
      setNotification({
        title: 'Error',
        message:
          error?.data?.error_message || 'Failed to delete volume. Please try again.',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="delete-encounter-dialog"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
    >
      <Dialog.Content>
        <Box className="p-6" onClick={(e) => e.stopPropagation()}>
          <Typography
            data-testid="delete-volume-dialog-title"
            className="text-neutral-800 mb-4"
          >
            Are you sure you want to delete volume &quot;{volumeToDelete?.label}
            &quot;? This action cannot be undone.
          </Typography>
          <Box className="flex justify-end gap-3 mt-6">
            <Button
              variant="outlined"
              color="neutral"
              onClick={onClose}
              className="px-4"
              data-testid="delete-encounter-dialog-cancel-button"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={handleDeleteConfirm}
              className="px-4"
              data-testid="delete-encounter-dialog-confirm-button"
              loading={isDeleting}
              disabled={isDeleting}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeleteEncounterDialog;
